import { useQuery, useQueryClient } from "@tanstack/react-query";
// PageHeader replaces the icon + back-link composite.
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { ShiftDefinitionValidationBadge } from "../components/attendance/ShiftDefinitionValidationBadge";
import { api } from "../lib/api";

export function ShiftDetailPage() {
  const { t } = useTranslation();
  const { shiftId } = useParams<{ shiftId: string }>();
  const qc = useQueryClient();

  const shQ = useQuery({
    queryKey: ["shift", shiftId],
    queryFn: () => api.getShift(shiftId!),
    enabled: !!shiftId,
  });

  if (shQ.isLoading) return <Loading />;
  if (shQ.error) return <ErrorPanel message={(shQ.error as Error).message} />;
  if (!shQ.data) return <ErrorPanel message={t("policies.shifts_not_found")} />;

  const s = shQ.data;

  const DAY_LABELS = [
    t("operations.leave_cal_day_sun"),
    t("operations.leave_cal_day_mon"),
    t("operations.leave_cal_day_tue"),
    t("operations.leave_cal_day_wed"),
    t("operations.leave_cal_day_thu"),
    t("operations.leave_cal_day_fri"),
    t("operations.leave_cal_day_sat"),
  ];

  const patch = (patch: Record<string, unknown>) =>
    api.updateShift(s.id, patch).then((updated) => {
      qc.setQueryData(["shift", s.id], updated);
      qc.invalidateQueries({ queryKey: ["shifts"] });
      return updated;
    });

  const toggleDay = (idx: number) => {
    const next = [...s.days_of_week];
    next[idx] = !next[idx];
    patch({ days_of_week: next });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${s.code} · ${s.name_en}`}
        breadcrumbs={[
          { label: t("policies.shifts_back"), href: "/policies/shifts" },
          { label: s.code },
        ]}
        actions={
          !s.is_active ? (
            <Badge tone="slate">{t("policies.loan_products_inactive_badge")}</Badge>
          ) : undefined
        }
      />

      <ShiftDefinitionValidationBadge shiftId={s.id} />

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.shifts_section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.name_en_label")}
            value={s.name_en}
            onSave={(v) => patch({ name_en: v })}
          />
          <EditableField
            label={t("policies.name_ar_label")}
            value={s.name_ar}
            rtl
            onSave={(v) => patch({ name_ar: v })}
          />
          <EditableField
            label={t("policies.description_label")}
            value={s.description}
            kind="textarea"
            onSave={(v) => patch({ description: v })}
          />
          <EditableField
            label={t("policies.active_label")}
            value={s.is_active}
            kind="boolean"
            onSave={(v) => patch({ is_active: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.shifts_section_schedule")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.shifts_start_long")}
            value={s.start_time}
            onSave={(v) => patch({ start_time: v })}
          />
          <EditableField
            label={t("policies.shifts_end_long")}
            value={s.end_time}
            onSave={(v) => patch({ end_time: v })}
          />
          <EditableField
            label={t("policies.shifts_timezone")}
            value={s.timezone}
            onSave={(v) => patch({ timezone: v })}
          />
          <EditableField
            label={t("policies.shifts_grace_minutes")}
            value={s.grace_period_minutes}
            kind="number"
            onSave={(v) => patch({ grace_period_minutes: Number(v) })}
          />
          <EditableField
            label={t("policies.shifts_break_minutes")}
            value={s.break_duration_minutes}
            kind="number"
            onSave={(v) => patch({ break_duration_minutes: Number(v) })}
          />
        </div>
        <div className="mt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("policies.shifts_active_days")}
          </div>
          <div className="flex flex-wrap gap-2">
            {DAY_LABELS.map((label, idx) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  s.days_of_week[idx]
                    ? "border-brand-blue bg-brand-blue text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {t("policies.shifts_sun_idx_hint")}
          </p>
        </div>
      </section>

      {/* M3X.ATTENDANCE F.1 — Section B: Timing deepening */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("shifts.section_timing_deep", "Section B — Timing (deep)")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("shifts.grace_arrival", "Grace period (arrival, min)")}
            value={s.grace_period_minutes_arrival ?? null}
            kind="number"
            onSave={(v) => patch({ grace_period_minutes_arrival: Number(v) })}
          />
          <EditableField
            label={t("shifts.grace_departure", "Grace period (departure, min)")}
            value={s.grace_period_minutes_departure ?? null}
            kind="number"
            onSave={(v) => patch({ grace_period_minutes_departure: Number(v) })}
          />
          <EditableField
            label={t("shifts.crosses_midnight", "Crosses midnight")}
            value={s.crosses_midnight ?? false}
            kind="boolean"
            onSave={(v) => patch({ crosses_midnight: v })}
          />
          <EditableField
            label={t("shifts.is_night", "Is night shift (Art 108 routing)")}
            value={s.is_night_shift ?? false}
            kind="boolean"
            onSave={(v) => patch({ is_night_shift: v })}
          />
          <EditableField
            label={t("shifts.auto_break_after", "Auto break after (min)")}
            value={s.auto_break_after_minutes ?? null}
            kind="number"
            onSave={(v) => patch({ auto_break_after_minutes: Number(v) })}
          />
        </div>
      </section>

      {/* M3X.ATTENDANCE F.1 — Section C: Ramadan + Special Periods */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("shifts.section_ramadan", "Section C — Ramadan + Special Periods")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("shifts.ramadan_start", "Ramadan start time")}
            value={s.ramadan_start_time ?? ""}
            onSave={(v) => patch({ ramadan_start_time: v })}
          />
          <EditableField
            label={t("shifts.ramadan_end", "Ramadan end time")}
            value={s.ramadan_end_time ?? ""}
            onSave={(v) => patch({ ramadan_end_time: v })}
          />
          <EditableField
            label={t(
              "shifts.ramadan_non_muslims",
              "Apply Ramadan to non-Muslims",
            )}
            value={s.ramadan_applies_to_non_muslims ?? false}
            kind="boolean"
            onSave={(v) => patch({ ramadan_applies_to_non_muslims: v })}
          />
          <EditableField
            label={t("shifts.summer_active", "Summer schedule active")}
            value={s.summer_schedule_active ?? false}
            kind="boolean"
            onSave={(v) => patch({ summer_schedule_active: v })}
          />
          <EditableField
            label={t("shifts.holiday_eve_short", "Holiday-eve short hours")}
            value={s.holiday_eve_short_hours ?? false}
            kind="boolean"
            onSave={(v) => patch({ holiday_eve_short_hours: v })}
          />
        </div>
      </section>

      {/* M3X.ATTENDANCE F.1 — Section D: Working Hours Calculation */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("shifts.section_working_hours", "Section D — Working Hours")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("shifts.minutes_per_day", "Scheduled minutes/day")}
            value={s.scheduled_minutes_per_day ?? null}
            kind="number"
            onSave={(v) => patch({ scheduled_minutes_per_day: Number(v) })}
          />
          <EditableField
            label={t("shifts.minutes_per_week", "Scheduled minutes/week (Art 98 cap 2880)")}
            value={s.scheduled_minutes_per_week ?? null}
            kind="number"
            onSave={(v) => patch({ scheduled_minutes_per_week: Number(v) })}
          />
          <EditableField
            label={t("shifts.weekly_rest_day", "Weekly rest day (Art 101)")}
            value={s.weekly_rest_day ?? ""}
            onSave={(v) => patch({ weekly_rest_day: v })}
          />
          <EditableField
            label={t("shifts.count_holidays", "Count holidays as worked")}
            value={s.count_holidays_as_worked ?? false}
            kind="boolean"
            onSave={(v) => patch({ count_holidays_as_worked: v })}
          />
          <EditableField
            label={t("shifts.auto_clock_out", "Auto clock-out after (min)")}
            value={s.auto_clock_out_after_minutes ?? null}
            kind="number"
            onSave={(v) => patch({ auto_clock_out_after_minutes: Number(v) })}
          />
          <EditableField
            label={t(
              "shifts.early_dep_as_early",
              "Clock-out before shift end = early departure",
            )}
            value={s.consider_clock_out_before_shift_end_as_early_departure ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({
                consider_clock_out_before_shift_end_as_early_departure: v,
              })
            }
          />
        </div>
      </section>

      {/* M3X.ATTENDANCE F.1 — Section E: Mutaba'aa floor */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("shifts.section_mutabaa_floor", "Section E — Mutaba'aa Floor (KSA Labor Law)")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "shifts.min_rest_hours",
              "Min rest hours between shifts (Art 104, floor 11h)",
            )}
            value={s.min_rest_hours_between_shifts ?? null}
            kind="number"
            onSave={(v) =>
              patch({ min_rest_hours_between_shifts: Number(v) })
            }
          />
          <EditableField
            label={t(
              "shifts.max_consec_days",
              "Max consecutive working days (Art 101, floor 6)",
            )}
            value={s.max_consecutive_working_days ?? null}
            kind="number"
            onSave={(v) =>
              patch({ max_consecutive_working_days: Number(v) })
            }
          />
          <EditableField
            label={t(
              "shifts.gender_restriction",
              "Gender restriction (any/male/female, Art 108)",
            )}
            value={s.gender_restriction ?? ""}
            onSave={(v) => patch({ gender_restriction: v })}
          />
          <EditableField
            label={t(
              "shifts.juveniles_allowed",
              "Applicable for juveniles (Art 159 routing)",
            )}
            value={s.applicable_for_juveniles ?? false}
            kind="boolean"
            onSave={(v) => patch({ applicable_for_juveniles: v })}
          />
        </div>
      </section>

      {/* Bilingual description (Section A split) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("shifts.section_description", "Section A — Bilingual Description")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("shifts.description_en", "Description (EN)")}
            value={s.description_en ?? ""}
            kind="textarea"
            onSave={(v) => patch({ description_en: v })}
          />
          <EditableField
            label={t("shifts.description_ar", "Description (AR)")}
            value={s.description_ar ?? ""}
            kind="textarea"
            rtl
            onSave={(v) => patch({ description_ar: v })}
          />
        </div>
      </section>
    </div>
  );
}
